// ==============================================
// Backup Password Banner
// ==============================================
//
// Accounts created by setting up a Scout have no password — they only ever tap
// an emailed link. That works until someone cannot reach their email: a dead
// phone, a changed address, no signal at the marina. So the dashboard suggests
// adding a password as a second way in.
//
// A banner, never a dialog: this is a suggestion, and nothing about the
// dashboard should be blocked by it. Dismissing is stored against the account
// (not the browser), so a "no" is not re-asked on the next device.

import { useState } from 'react';
import { KeyIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useAuth } from '../../providers/AuthProvider';

export function BackupPasswordBanner() {
  const { user, refreshUser } = useAuth();
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only accounts with no password, who have not already said no. `undefined`
  // means an older API response that predates the field — say nothing rather
  // than guess.
  // Not on a Scout's own sign-in: the API refuses a first password from it.
  const shouldShow =
    !hidden &&
    user?.has_password === false &&
    !user?.password_prompt_dismissed_at &&
    user?.signed_in_by_scout !== true;

  if (!shouldShow) return null;

  const dismiss = async () => {
    setHidden(true);
    try {
      await api.dismissPasswordPrompt();
      await refreshUser?.();
    } catch {
      // The banner is already gone for this session; a failed write only means
      // it may reappear next time, which is not worth interrupting anyone over.
    }
  };

  const save = async () => {
    if (password.length < 8) {
      setError('Passwords need to be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Those two passwords do not match');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.setFirstPassword(password);
      setHidden(true);
      await refreshUser?.();
    } catch {
      setError('That did not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-cyan-500/50 bg-cyan-500/10 p-4">
      <div className="flex items-start gap-3">
        <KeyIcon className="w-5 h-5 text-cyan-400 flex-none mt-0.5" />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Add a password as a backup</p>
          <p className="mt-0.5 text-sm text-slate-400">
            You sign in with an emailed link. A password gives you a second way in if you ever
            can&rsquo;t get to your email.
          </p>

          {!expanded && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="mt-2 text-sm font-semibold text-cyan-400 hover:underline"
            >
              Set a password
            </button>
          )}

          {expanded && (
            <div className="mt-3 space-y-3 max-w-sm">
              <div>
                <label htmlFor="backup-password" className="block text-xs font-medium text-slate-300 mb-1">
                  New password
                </label>
                <input
                  id="backup-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label htmlFor="backup-password-confirm" className="block text-xs font-medium text-slate-300 mb-1">
                  Confirm password
                </label>
                <input
                  id="backup-password-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Type it again"
                />
              </div>

              {error && <p className="text-sm text-red-400">{error}</p>}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => void save()}
                  disabled={busy}
                  className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg text-sm font-semibold hover:from-cyan-400 hover:to-blue-500 transition-all disabled:opacity-50"
                >
                  {busy ? 'Saving…' : 'Save password'}
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded(false)}
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>

              <p className="text-xs text-slate-500">
                Emailed links keep working either way — this is an extra way in, not a replacement.
              </p>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => void dismiss()}
          aria-label="Dismiss"
          title="Don't suggest this again"
          className="flex-none text-slate-400 hover:text-white transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default BackupPasswordBanner;
